import { CategoryCard } from '#/components/ui/CategoryCard';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { SECTOR_DESCRIPTIONS, SECTOR_LABELS } from '#/lib/category-navigation';
import { UnreachableCaseError } from '#/lib/error';
import { MARKET_CAP_CATEGORY_RANGE_LABEL } from '#/lib/market-cap-category';
import type { SectorSlug } from '#/types';

type TopicKind = 'sector' | 'industry' | 'market-cap-category';

interface EquityCategorySectionProps {
  sectorSlug?: SectorSlug | null;
  industry?: string | null;
  marketCapCategory?: string | null;
}

interface CategoryTopic {
  kind: TopicKind;
  eyebrow: string;
  title: string;
  description?: string;
  href?: string;
}

const DEFAULT_DESCRIPTION_BY_KIND: Record<TopicKind, string> = {
  sector:
    'Companies grouped by the broad segment of the market they operate in.',
  industry:
    'Companies grouped by their primary line of business within a broader sector.',
  'market-cap-category':
    'A company size classification based on the total market value of its outstanding shares.',
};

const INDUSTRY_DESCRIPTION_BY_LABEL: Partial<Record<string, string>> = {
  Semiconductors:
    'Companies that design, manufacture, or supply the chips that power modern electronics.',
  Software:
    'Businesses developing applications, platforms, and enterprise systems for digital workflows.',
  Biotechnology:
    'Companies researching and commercializing treatments derived from biological science.',
  Banks:
    'Businesses focused on lending, deposits, payments, and related financial services.',
};

function getDescription(
  kind: TopicKind,
  title: string,
  sectorSlug?: SectorSlug | null,
) {
  switch (kind) {
    case 'sector':
      return sectorSlug
        ? SECTOR_DESCRIPTIONS[sectorSlug]
        : DEFAULT_DESCRIPTION_BY_KIND[kind];
    case 'industry':
      return (
        INDUSTRY_DESCRIPTION_BY_LABEL[title] ??
        DEFAULT_DESCRIPTION_BY_KIND[kind]
      );
    case 'market-cap-category':
      return (
        MARKET_CAP_CATEGORY_RANGE_LABEL[title] ??
        DEFAULT_DESCRIPTION_BY_KIND[kind]
      );
    default:
      throw new UnreachableCaseError(kind);
  }
}

function getTopics(
  sectorSlug?: SectorSlug | null,
  industryLabel?: string | null,
  marketCapCategoryLabel?: string | null,
): CategoryTopic[] {
  const topics: CategoryTopic[] = [];
  const industry = industryLabel?.trim();
  const marketCapCategory = marketCapCategoryLabel?.trim();

  if (sectorSlug) {
    topics.push({
      kind: 'sector',
      eyebrow: 'Sector',
      title: SECTOR_LABELS[sectorSlug],
      description: getDescription(
        'sector',
        SECTOR_LABELS[sectorSlug],
        sectorSlug,
      ),
      href: `/${sectorSlug}`,
    });
  }

  if (industry) {
    topics.push({
      kind: 'industry',
      eyebrow: 'Industry',
      title: industry,
      description: getDescription('industry', industry),
    });
  }

  if (marketCapCategory) {
    topics.push({
      kind: 'market-cap-category',
      eyebrow: 'Market Cap Category',
      title: marketCapCategory,
      description: getDescription('market-cap-category', marketCapCategory),
    });
  }

  return topics;
}

export function EquityCategorySection({
  sectorSlug,
  industry,
  marketCapCategory,
}: EquityCategorySectionProps) {
  const topics = getTopics(sectorSlug, industry, marketCapCategory);

  if (topics.length === 0) {
    return null;
  }

  return (
    <section
      id="related"
      className="flow flow-inner-20 scroll-mt-(--scroll-offset)"
    >
      <SectionHeader title="Related to This Equity" actionAlignment="center" />

      <div className="grid gap-12 md:grid-cols-3">
        {topics.map((topic) => (
          <CategoryCard
            key={topic.kind}
            eyebrow={topic.eyebrow}
            title={topic.title}
            description={topic.description}
            to={topic.href}
          />
        ))}
      </div>
    </section>
  );
}
